import type { MessageDescriptor } from 'react-intl';

import DiscoveryFeedbackIcon from './images/DiscoveryFeedbackIcon.svg';
import DiscoveryFeedbackIllustrationOneColumn from './images/DiscoveryFeedbackIllustrationOneColumn.svg';
import DiscoveryFeedbackIllustrationTwoColumn from './images/DiscoveryFeedbackIllustrationTwoColumn.svg';
import ProjectPlannerIcon from './images/ProjectPlannerIcon.svg';
import PlannerIllustrationOneColumn from './images/PlannerIllustrationOneColumn.svg';
import PlannerIllustrationTwoColumn from './images/PlannerIllustrationTwoColumn.svg';
import { i18n } from './messages.ts';

/**
 * Static configuration for the "Rovo Agents for you" cards.
 *
 * PR4 (CCCEMAU-3070): the `agentExternalConfigReference` field is now wired up:
 * clicking "Try it" publishes a `chat-new` event that opens the referenced OOTB
 * agent in the Rovo sidebar (see AgentCard.tsx). We reference the OOTB agent by
 * its external config reference rather than an agent id, since the underlying
 * agent id can change while the OOTB reference is stable.
 *
 * Agent `icon` (top-left badge) and `illustrations` assets are product SVGs
 * exported from Figma (colours baked in) and rendered as `<img>` sources.
 */
export type AgentCardLayout = 'oneColumn' | 'twoColumn';

export type AgentCardConfig = {
	/** Stable key used for React keys, test ids and (later) dismiss persistence. */
	key: string;
	/**
	 * External config reference for the OOTB agent (the stable identifier used to
	 * launch it). Preferred over a raw agent id because the agent id can change
	 * while the OOTB reference is stable.
	 */
	agentExternalConfigReference: string;
	/** URL to the agent's top-left badge SVG icon asset (colours baked in). */
	icon: string;
	/** Decorative illustration variant for each visible-card layout. */
	illustrations: Record<AgentCardLayout, string>;
	title: MessageDescriptor;
	description: MessageDescriptor;
};

export const AGENT_CARDS: AgentCardConfig[] = [
	{
		key: 'planner',
		agentExternalConfigReference: 'planner_agent',
		icon: ProjectPlannerIcon,
		illustrations: {
			oneColumn: PlannerIllustrationOneColumn,
			twoColumn: PlannerIllustrationTwoColumn,
		},
		title: i18n.projectPlannerTitle,
		description: i18n.projectPlannerDescription,
	},
	{
		key: 'discovery-feedback',
		agentExternalConfigReference: 'discovery_and_feedback_agent',
		icon: DiscoveryFeedbackIcon,
		illustrations: {
			oneColumn: DiscoveryFeedbackIllustrationOneColumn,
			twoColumn: DiscoveryFeedbackIllustrationTwoColumn,
		},
		title: i18n.discoveryFeedbackTitle,
		description: i18n.discoveryFeedbackDescription,
	},
];
